// Shared schema.org entities for the marketing site.
//
// Every page links to the same Organization, WebSite, SoftwareApplication and Person
// through stable `@id`s, so search engines and AI assistants see one consistent entity
// ("NerdCRM, made by Nerddevs, written about by Md Sohel Rana") instead of loose copies.
// The Organization and WebSite nodes are also in index.html (static, on every page).
//
// Keep facts here in sync with content/pricing/main.md and seo/llms-intro.md.

export const SITE_URL = 'https://getnerdcrm.com'

export const ORG_ID = 'https://nerddevs.com/#organization'
export const WEBSITE_ID = `${SITE_URL}/#website`
export const SOFTWARE_ID = `${SITE_URL}/#software`
export const FOUNDER_ID = `${SITE_URL}/#founder`

export const ORG_LOGO = `${SITE_URL}/icons/web-app-manifest-512x512.png`

/** Short reference to the publisher, for use inside other nodes. */
export const orgRef = {
  '@type': 'Organization',
  '@id': ORG_ID,
  name: 'Nerddevs',
  url: 'https://nerddevs.com',
  logo: { '@type': 'ImageObject', url: ORG_LOGO, width: 512, height: 512 },
}

export interface SocialLink {
  label: string
  url: string
}

export const FOUNDER = {
  name: 'Md Sohel Rana',
  jobTitle: 'Founder and CEO, Nerddevs',
  photo: `${SITE_URL}/og/sohel-headshot.jpg`,
  links: [
    { label: 'LinkedIn', url: 'https://www.linkedin.com/in/mdsohelrana/' },
    { label: 'X', url: 'https://x.com/sohel023010' },
    { label: 'GitHub', url: 'https://github.com/sohel-rana' },
    { label: 'Website', url: 'https://mdsohelrana.com' },
  ] as SocialLink[],
}

export function absoluteUrl(url: string | undefined): string | undefined {
  if (!url) return undefined
  return url.startsWith('http') ? url : `${SITE_URL}${url.startsWith('/') ? '' : '/'}${url}`
}

export function personLd(opts: {
  name?: string
  jobTitle?: string
  photo?: string
  sameAs?: SocialLink[]
  withContext?: boolean
} = {}) {
  const isFounder = !opts.name || opts.name === FOUNDER.name
  // The founder always carries his full set of profiles, plus any the page adds.
  const links = [...(opts.sameAs || []), ...(isFounder ? FOUNDER.links : [])]
  const sameAs = [...new Set(links.map((s) => s.url.replace(/\/$/, '')))]
  const image = absoluteUrl(opts.photo) || (isFounder ? FOUNDER.photo : undefined)
  return {
    ...(opts.withContext ? { '@context': 'https://schema.org' } : {}),
    '@type': 'Person',
    ...(isFounder ? { '@id': FOUNDER_ID } : {}),
    name: opts.name || FOUNDER.name,
    jobTitle: opts.jobTitle || FOUNDER.jobTitle,
    worksFor: { '@id': ORG_ID, '@type': 'Organization', name: 'Nerddevs' },
    ...(image ? { image } : {}),
    ...(sameAs.length ? { sameAs } : {}),
  }
}

// Offers are valid until the end of next year, computed at build time, so the date
// never lapses as long as the site is rebuilt (it is, daily).
const PRICE_VALID_UNTIL = `${new Date().getFullYear() + 1}-12-31`

function perUserOffer(name: string, price: string, description: string) {
  return {
    '@type': 'Offer',
    name,
    description,
    price,
    priceCurrency: 'USD',
    priceSpecification: {
      '@type': 'UnitPriceSpecification',
      price,
      priceCurrency: 'USD',
      unitText: 'user/month',
      billingDuration: 'P1M',
      referenceQuantity: { '@type': 'QuantitativeValue', value: 1, unitText: 'user' },
    },
    availability: 'https://schema.org/InStock',
    eligibleRegion: { '@type': 'Place', name: 'Worldwide' },
    priceValidUntil: PRICE_VALID_UNTIL,
    url: `${SITE_URL}/pricing`,
  }
}

export function softwareApplicationLd() {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': SOFTWARE_ID,
    name: 'NerdCRM',
    alternateName: 'Nerd CRM',
    description:
      'NerdCRM is a $4 per user per month CRM for small phone-heavy sales teams. Call recordings from MicroSIP (Windows) or Nerd Dial (Mac) are linked to the right contact and deal automatically, WhatsApp campaigns and cold email run from the same contact list, and an optional AI Call Analysis add-on transcribes Bangla and English calls and qualifies every lead.',
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'CRM',
    operatingSystem: 'Web, Windows, macOS, iOS, Android',
    url: `${SITE_URL}/`,
    image: `${SITE_URL}/og/default.png`,
    // Third-party profiles of the product. Add Capterra, SourceForge, SaaSworthy,
    // Crozdesk and Product Hunt here as each listing goes live.
    sameAs: [
      'https://www.g2.com/products/nerdcrm/reviews',
      'https://alternativeto.net/software/nerdcrm/about/',
    ],
    inLanguage: 'en',
    countriesSupported: 'Worldwide',
    publisher: { '@id': ORG_ID },
    creator: { '@id': ORG_ID },
    offers: [
      {
        '@type': 'Offer',
        name: 'Free plan',
        description:
          'Free for up to 5 users, no credit card. Includes a one-time allowance of 100 call recordings, 100 WhatsApp messages and 300 outreach emails.',
        price: '0',
        priceCurrency: 'USD',
        availability: 'https://schema.org/InStock',
        eligibleRegion: { '@type': 'Place', name: 'Worldwide' },
        url: `${SITE_URL}/pricing`,
      },
      perUserOffer(
        'NerdCRM',
        '4',
        'Call recording auto-link, WhatsApp outreach, cold email, pipeline, KAM and sales reports, payment tracking and integrations.',
      ),
      perUserOffer(
        'AI Call Analysis add-on',
        '5',
        'Transcribes and scores recorded calls (Bangla and English) and qualifies every lead. 300 analysed calls per user per month, pooled across the team.',
      ),
    ],
    featureList: [
      'Call recording auto-linked to contacts and deals by phone number (MicroSIP on Windows, Nerd Dial on Mac)',
      'AI call analysis and lead qualification for Bangla and English calls',
      'WhatsApp Cloud API template campaigns with a shared reply inbox and automatic opt-outs',
      'Cold email outreach sequences with a unified reply inbox',
      'Drag-and-drop Kanban pipeline management',
      'Daily activity timeline with every dial, answered or not',
      'Natural-language Smart Reports and Deal Cadence',
      'KAM and sales reports',
      'Payment tracking and due-payment management',
      'CSV import/export, Facebook Ads lead import, webhooks and API keys',
      'Multi-tenant organizations with seven roles',
    ],
  }
}
